import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Checkbox, 
  FormControlLabel, 
  Link, 
  InputAdornment, 
  IconButton,
  Container,
  Grid,
  Paper,
  FormHelperText,
  Skeleton,
  Fade,
  useMediaQuery,
  useTheme,
  Tooltip
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Login as LoginIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useAuth } from '../context/AuthContext';
import { useSnackbar } from 'notistack';
import { motion } from 'framer-motion';

const SignIn = () => {
  const navigate = useNavigate();
  const { login, loading, error, setError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // Initial loading effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  // Validation functions
  const validateEmail = (email) => {
    if (!email) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return null;
  };

  const validatePassword = (password) => {
    if (!password) return 'Password is required';
    if (password.length < 6) return 'Password must be at least 6 characters';
    return null;
  };

  // Real-time validation
  const validateField = (field, value) => {
    let error = null;
    switch (field) {
      case 'email':
        error = validateEmail(value);
        break;
      case 'password':
        error = validatePassword(value);
        break;
      default:
        break;
    }
    
    setValidationErrors(prev => ({
      ...prev,
      [field]: error
    }));
    
    return error === null;
  };

  const handleFieldChange = (field, value) => {
    switch (field) {
      case 'email':
        setEmail(value);
        break;
      case 'password':
        setPassword(value);
        break;
      default:
        break;
    }
    
    // Validate on change if field has been touched
    if (touched[field]) {
      validateField(field, value);
    }
  };

  const handleFieldBlur = (field, value) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, value);
  };

  const validateForm = () => {
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);
    
    setValidationErrors({
      email: emailError,
      password: passwordError
    });
    
    setTouched({
      email: true,
      password: true
    });
    
    return !emailError && !passwordError;
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError(null); // Clear any previous auth errors
    
    if (!validateForm()) {
      enqueueSnackbar('Please fix the validation errors', { variant: 'error' });
      return;
    }

    try {
      const result = await login({ email, password, remember: rememberMe });
      if (result.ok) {
        enqueueSnackbar('Login successful! Redirecting...', { variant: 'success' });
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        enqueueSnackbar(result.error || 'Login failed', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar('An unexpected error occurred', { variant: 'error' });
    }
  };

  const currentYear = new Date().getFullYear();

  if (isInitialLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        minHeight: '100vh',
        bgcolor: '#f5f5f5'
      }}>
        <Box sx={{ 
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          width: '40%',
          background: 'linear-gradient(135deg, #0FABDB 0%, #10C4DB 100%)',
          p: 4
        }}>
          <Skeleton variant="rectangular" width="60%" height={120} sx={{ bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }} />
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
            <Skeleton variant="text" width="80%" height={40} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
            <Skeleton variant="text" width="60%" height={30} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
          </Box>
          <Skeleton variant="text" width="100%" height={20} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
        </Box>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Container maxWidth="sm">
            <Box sx={{ p: { xs: 3, sm: 5 } }}>
              <Skeleton variant="text" width="70%" height={40} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="50%" height={20} sx={{ mb: 4 }} />
              <Skeleton variant="rectangular" width="100%" height={56} sx={{ mb: 3, borderRadius: 1.5 }} />
              <Skeleton variant="rectangular" width="100%" height={56} sx={{ mb: 3, borderRadius: 1.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Skeleton variant="text" width="40%" height={20} />
                <Skeleton variant="text" width="30%" height={20} />
              </Box>
              <Skeleton variant="rectangular" width="100%" height={48} sx={{ borderRadius: 1.5 }} />
            </Box>
          </Container>
        </Box>
      </Box>
    );
  }

  return (
    <Fade in={!isInitialLoading} timeout={800}>
      <Box sx={{ 
        display: 'flex', 
        minHeight: '100vh',
        bgcolor: '#f5f5f5'
      }}>
        {/* Left Side - Gradient Background with Logo */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          style={{ display: isMobile ? 'none' : 'flex', width: '40%' }}
        >
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            background: 'linear-gradient(135deg, #0FABDB 0%, #10C4DB 100%)',
            p: 4,
            color: 'white',
            justifyContent: 'space-between'
          }}>
        {/* Logo Area */}
        <Box sx={{ mt: 6, ml: 4,mb: 8 }}>
          <Box 
            sx={{ 
              bgcolor: 'white', 
              borderRadius: 2, 
              py: .5, 
              px: 2.5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: 'fit-content'
            }}
          >
            <Typography 
              variant="h2" 
              component="div" 
              sx={{ 
                fontWeight: 'bold', 
                color: '#12C1DC',
                letterSpacing: 1,
                fontSize: '6rem'
              }}
            >
              APEX
            </Typography>
            <Typography 
              variant="subtitle1" 
              component="div" 
              sx={{ 
                color: '#12C1DC',
                letterSpacing: 3.5,
                textTransform: 'uppercase',
                fontSize: '1.5rem',
                mt: -1.5
              }}
            >
              PAYROLL SYSTEM
            </Typography>
          </Box>
        </Box>
        
        {/* Welcome Text */}
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1, ml: 4 }}>
          <Typography variant="h4" fontWeight="500" sx={{ mb: 1, fontSize: '2rem' }}>
            Welcome to APEX!
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1rem' }}>
            Applicant Employee Experience
          </Typography>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 'auto', fontSize: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box>© {currentYear} Sparkle Star International Corporation</Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Link href="#" color="inherit">Privacy</Link>
            <Link href="#" color="inherit">Legal</Link>
            <Link href="#" color="inherit">Contact</Link>
          </Box>
          </Box>
          </Box>
        </motion.div>

        {/* Right Side - Login Form */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          style={{ flex: 1 }}
        >
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            px: { xs: 2, sm: 4 },
            py: { xs: 4, sm: 0 },
            bgcolor: 'white',
            minHeight: '100vh'
          }}>
            <Container maxWidth="sm">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Box 
                  component="form" 
                  onSubmit={handleSignIn}
                  sx={{ 
                    bgcolor: 'white',
                    borderRadius: 2,
                    p: { xs: 3, sm: 5 },
                    boxShadow: { xs: '0px 4px 20px rgba(0, 0, 0, 0.05)', md: 'none' },
                    maxWidth: '100%'
                  }}
                >
                  <Box sx={{ textAlign: 'left', mb: 4 }}>
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.6 }}
                    >
                      <Typography 
                        variant={isMobile ? "h6" : "h5"} 
                        component="h1" 
                        fontWeight="500" 
                        color="text.primary"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                      >
                        <SecurityIcon sx={{ color: '#12C1DC', fontSize: { xs: '1.2rem', md: '1.5rem' } }} />
                        Login with <Typography component="span" color="#12C1DC" variant={isMobile ? "h6" : "h5"} fontWeight="bold">APEX</Typography>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Enter your credentials to continue
                      </Typography>
                    </motion.div>
                  </Box>

                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                    </motion.div>
                  )}

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                  >
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ mb: 1 }}>
                        Email address
                      </Typography>
                      <TextField
                        fullWidth
                        id="email"
                        type="email"
                        placeholder="Type your email address here"
                        variant="outlined"
                        value={email}
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                        onBlur={(e) => handleFieldBlur('email', e.target.value)}
                        error={touched.email && !!validationErrors.email}
                        helperText={touched.email && validationErrors.email}
                        disabled={loading}
                        InputProps={{
                          sx: { 
                            borderRadius: 1.5,
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#35CFF7',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#12C1DC',
                            }
                          }
                        }}
                        FormHelperTextProps={{
                          sx: { 
                            mx: 0, 
                            mt: 0.5,
                            fontSize: '0.75rem'
                          }
                        }}
                        sx={{ 
                          borderBottom: '4px solid #12C1DC', 
                          borderRadius: '10px',
                          '& .MuiInputBase-input': {
                            fontSize: { xs: '0.9rem', md: '1rem' }
                          }
                        }}
                      />
                    </Box>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.8 }}
                  >
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ mb: 1 }}>
                        Enter your Password
                      </Typography>
                      <TextField
                        fullWidth
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Type your password here"
                        variant="outlined"
                        value={password}
                        onChange={(e) => handleFieldChange('password', e.target.value)}
                        onBlur={(e) => handleFieldBlur('password', e.target.value)}
                        error={touched.password && !!validationErrors.password}
                        helperText={touched.password && validationErrors.password}
                        disabled={loading}
                        InputProps={{
                          sx: { 
                            borderRadius: 1.5,
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#35CFF7',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#12C1DC',
                            }
                          },
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title={showPassword ? "Hide password" : "Show password"}>
                                <IconButton
                                  onClick={handleTogglePassword}
                                  edge="end"
                                  disabled={loading}
                                  sx={{
                                    color: '#666',
                                    '&:hover': {
                                      color: '#12C1DC'
                                    }
                                  }}
                                >
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                        FormHelperTextProps={{
                          sx: { 
                            mx: 0, 
                            mt: 0.5,
                            fontSize: '0.75rem'
                          }
                        }}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontSize: { xs: '0.9rem', md: '1rem' }
                          }
                        }}
                      />
                    </Box>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.9 }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between', 
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      mb: 3,
                      gap: { xs: 2, sm: 0 }
                    }}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            disabled={loading}
                            sx={{ 
                              '&.Mui-checked': {
                                color: '#12C1DC',
                              }
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2" color={loading ? 'text.disabled' : 'text.primary'}>
                            Remember me!
                          </Typography>
                        }
                      />
                      <Link 
                        href="#" 
                        sx={{ 
                          color: loading ? '#ccc' : '#12C1DC', 
                          textDecoration: 'none',
                          pointerEvents: loading ? 'none' : 'auto',
                          fontSize: { xs: '0.875rem', md: '1rem' },
                          '&:hover': {
                            textDecoration: loading ? 'none' : 'underline'
                          }
                        }}
                      >
                        Forgot Password?
                      </Link>
                    </Box>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 1.0 }}
                  >
                    <Button
                      fullWidth
                      variant="contained"
                      type="submit"
                      startIcon={!loading ? <LoginIcon /> : null}
                      sx={{
                        bgcolor: '#12C1DC',
                        py: { xs: 1.2, md: 1.5 },
                        textTransform: 'none',
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        fontWeight: 'medium',
                        '&:hover': {
                          bgcolor: '#0FABDB',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(18, 193, 220, 0.4)'
                        },
                        '&:disabled': {
                          bgcolor: '#ccc',
                          color: 'white'
                        },
                        borderRadius: 1.5,
                        position: 'relative',
                        minHeight: { xs: '44px', md: '48px' },
                        transition: 'all 0.2s ease-in-out'
                      }}
                      disabled={loading || (touched.email && !!validationErrors.email) || (touched.password && !!validationErrors.password)}
                    >
                      {loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} sx={{ color: 'white' }} />
                          <Typography variant="button" sx={{ textTransform: 'none' }}>
                            Signing in...
                          </Typography>
                        </Box>
                      ) : (
                        'Log In'
                      )}
                    </Button>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 1.1 }}
                  >
                    <Box sx={{ textAlign: 'center', mt: 3 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                        Don't have an account?{' '}
                        <Link 
                          href="/register" 
                          sx={{ 
                            color: loading ? '#ccc' : '#12C1DC', 
                            textDecoration: 'none',
                            pointerEvents: loading ? 'none' : 'auto',
                            fontWeight: 'medium',
                            '&:hover': {
                              textDecoration: loading ? 'none' : 'underline'
                            }
                          }}
                        >
                          Create an account
                        </Link>.
                      </Typography>
                    </Box>
                  </motion.div>
                </Box>
              </motion.div>
            </Container>
          </Box>
        </motion.div>
      </Box>
    </Fade>
  );
};

export default SignIn;
